import { BaseSocketService, SocketServiceConfig } from '../index';

export interface DealerLaunchSocketEvent {
  dealer_id?: string;
  job_id?: string;
  message?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface DealerLaunchSubscription {
  dealer_id: string;
  callbacks: Set<(eventName: string, data: DealerLaunchSocketEvent) => void>;
  active: boolean;
}

const DEALER_LAUNCH_EVENT_PREFIX = 'dealer_launch_';

class DealerLaunchSocketService extends BaseSocketService {
  private static instance: DealerLaunchSocketService;
  private subscriptions: Map<string, DealerLaunchSubscription> = new Map();
  private eventsBound = false;

  constructor(config: SocketServiceConfig) {
    super(config);
    this.setupDealerLaunchEventListeners();
  }

  static getInstance(config?: SocketServiceConfig): DealerLaunchSocketService {
    if (!DealerLaunchSocketService.instance) {
      if (!config) {
        throw new Error(
          'DealerLaunchSocketService requires config for first initialization',
        );
      }
      DealerLaunchSocketService.instance = new DealerLaunchSocketService(config);
    }
    return DealerLaunchSocketService.instance;
  }

  getServiceName(): string {
    return 'DealerLaunchSocketService';
  }

  getEvents(): string[] {
    return [
      'dealer_launch_request_received',
      'dealer_launch_progress',
      'dealer_launch_completed',
      'subscribe_success',
      'subscribe_error',
      'unsubscribe_success',
      'unsubscribe_error',
    ];
  }

  subscribeToDealer(
    dealerId: string,
    callback: (eventName: string, data: DealerLaunchSocketEvent) => void,
  ): boolean {
    if (!dealerId || typeof callback !== 'function') {
      console.error(
        '❌ [DealerLaunch] Invalid dealer ID or callback for subscription',
      );
      return false;
    }

    if (this.subscriptions.has(dealerId)) {
      const existing = this.subscriptions.get(dealerId);
      if (existing) {
        existing.callbacks.add(callback);
      }
      return true;
    }

    this.subscriptions.set(dealerId, {
      dealer_id: dealerId,
      callbacks: new Set([callback]),
      active: true,
    });

    this.emit('subscribe', dealerId);

    console.log(
      `📡 [DealerLaunch] Subscribed to dealer: ${dealerId}${this.isConnected() ? '' : ' (queued - will send when connected)'}`,
    );
    return true;
  }

  unsubscribeFromDealer(
    dealerId: string,
    callback?: (eventName: string, data: DealerLaunchSocketEvent) => void,
  ): boolean {
    if (!dealerId) {
      console.error('❌ [DealerLaunch] Invalid dealer ID for unsubscription');
      return false;
    }

    const subscription = this.subscriptions.get(dealerId);
    if (!subscription) {
      return false;
    }

    if (callback) {
      subscription.callbacks.delete(callback);
      if (subscription.callbacks.size > 0) {
        return true;
      }
    }

    subscription.active = false;
    this.emit('unsubscribe', dealerId);
    this.subscriptions.delete(dealerId);

    console.log(`📡 [DealerLaunch] Unsubscribed from dealer: ${dealerId}`);
    return true;
  }

  getCurrentSubscribedDealerId(): string | null {
    const entry = Array.from(this.subscriptions.entries()).find(
      ([, sub]) => sub.active,
    );
    return entry ? entry[0] : null;
  }

  private handleIncomingEvent(eventName: string, data: DealerLaunchSocketEvent): void {
    try {
      if (!eventName.startsWith(DEALER_LAUNCH_EVENT_PREFIX)) {
        return;
      }

      this.addEventToHistory(eventName, data);

      const rawDealerId = data?.dealer_id ?? data?.message?.dealer_id;
      const dealerId =
        rawDealerId != null
          ? String(rawDealerId)
          : this.getCurrentSubscribedDealerId();
      if (!dealerId) return;

      let subscription = this.subscriptions.get(dealerId);
      if (
        !subscription?.active &&
        eventName.startsWith(DEALER_LAUNCH_EVENT_PREFIX) &&
        this.subscriptions.size === 1
      ) {
        const only = Array.from(this.subscriptions.values())[0];
        if (only?.active) subscription = only;
      }

      if (subscription?.active && subscription.callbacks.size > 0) {
        subscription.callbacks.forEach(cb => {
          if (typeof cb === 'function') {
            cb(eventName, data);
          }
        });
      }
    } catch (error) {
      console.error(
        `❌ [DealerLaunch] Error handling event '${eventName}':`,
        error,
      );
    }
  }

  private setupDealerLaunchEventListeners(): void {
    if (this.eventsBound) return;

    this.getEvents().forEach(eventName => {
      this.on(eventName, (data: DealerLaunchSocketEvent) => {
        this.handleIncomingEvent(eventName, data);
      });
    });

    this.eventsBound = true;
  }

  connect(): void {
    super.connect();
  }

  protected onReconnect(): void {
    super.onReconnect();
    const currentDealerId = this.getCurrentSubscribedDealerId();
    if (currentDealerId && this.isConnected()) {
      setTimeout(() => {
        if (this.isConnected()) {
          this.emit('subscribe', currentDealerId);
          console.log(
            `📡 [DealerLaunch] Re-subscribed to dealer after reconnect: ${currentDealerId}`,
          );
        }
      }, 100);
    }
  }
}

export const dealerLaunchSocketService = DealerLaunchSocketService.getInstance;
export { DealerLaunchSocketService };
